import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, map, take, tap } from 'rxjs/operators';
import { FieldConfig } from '../../shared/modules/configurable-form/models/field-configs/field-config.interface';
import { FormConfigLoader } from '../../shared/modules/configurable-form/services/form-config-loader';
import { _safe } from '../../shared/utils/safe-get.method';
import { config as _config } from '../configs';
import { ShippingLocationMode, ShippingLocationModeCode } from '../models/shipping-location-mode.model';
import { OneClickState } from '../reducers';
import {
  getOneClickBuilderParams,
  getOneClickBuilderShippingModeParams,
  getOneClickConfigParams,
} from '../selectors/one-click-config-loader.selector';
import {
  OneClickConfig,
  OneClickProfilesTableConfig,
} from '@ordersnproducts/modules/one-click-order/models/one-click-config';

const shippingLocationCodeMap = {
  [ShippingLocationModeCode.Jobsite]: 'views.one_click.jobsite',
  [ShippingLocationModeCode.PointOfDelivery]: 'views.one_click.pod',
};

@Injectable()
export class OneClickConfigLoaderService {
  constructor(private store: Store<OneClickState>, private configLoader: FormConfigLoader) {}

  /**
   * Retrieves one click draft config
   */
  getOneClickPreviewConfig(): Observable<FieldConfig[]> {
    return this.getOneClickConfig().pipe(
      tap(config => this.configLoader.registerEffectsFromConfig<OneClickConfig>(config, 'preview')),
      map(config => config.preview),
    );
  }

  /**
   * Retrieves one click profile creation config
   */
  getOneClickBuilderConfig(shippingMode: ShippingLocationMode): Observable<FieldConfig[]> {
    return this.store.pipe(
      select(getOneClickBuilderParams),
      filter(({ countryCode, shippingMethodCode }) => !!countryCode && !!shippingMethodCode),
      take(1),
      map(({ countryCode, productLineCode, shippingMethodCode }) => {
        const config = this.resolveConfig(countryCode, productLineCode, shippingMethodCode);
        if (config && config.builder && config.builder[shippingMode.code]) {
          return { config, shippingModeCode: shippingMode.code };
        } else {
          throw new Error(`No proper builder config found for ${countryCode}. Verify the builder config exists`);
        }
      }),
      tap(({ config, shippingModeCode }) =>
        this.configLoader.registerEffectsFromConfig(config.builder, shippingModeCode),
      ),
      map(({ config, shippingModeCode }) => config.builder[shippingModeCode]),
    );
  }

  public getOneClickShippingModes(): Observable<ShippingLocationMode[]> {
    return this.store.pipe(
      select(getOneClickBuilderShippingModeParams),
      filter(({ countryCode, shippingMethodCode }) => !!countryCode && !!shippingMethodCode),
      take(1),
      map(({ countryCode, productLineCode, shippingMethodCode }) => {
        return this.resolveConfig(countryCode, productLineCode, shippingMethodCode);
      }),
      map(config =>
        Object.keys(config.builder).map((shippingModeCode: ShippingLocationModeCode) => {
          return {
            code: shippingModeCode,
            desc: shippingLocationCodeMap[shippingModeCode],
          } as ShippingLocationMode;
        }),
      ),
    );
  }

  /**
   * Retrieves config for one click order preview
   */
  public getOneClickConfig(): Observable<OneClickConfig> {
    return this.store.pipe(
      select(getOneClickConfigParams),
      take(1),
      map(({ countryCode, businessLine, shippingConditionCode }) => {
        return this.resolveConfig(countryCode, businessLine, shippingConditionCode);
      }),
    );
  }

  /**
   * Retrieves config for one click profile table
   */
  public getOneClickProfileTableConfig(): Observable<OneClickProfilesTableConfig> {
    return this.store.pipe(
      select(getOneClickConfigParams),
      take(1),
      map(({ countryCode }) => {
        const countryConfig = this.resolveEntireCountryConfig(countryCode);
        return countryConfig.profilesTable as OneClickProfilesTableConfig;
      }),
    );
  }

  private resolveConfig(countryCode: string, productLineCode: string, shippingConditionCode: string): OneClickConfig {
    const config = _safe(() => _config[countryCode][productLineCode][shippingConditionCode]);
    if (!config) {
      throw new Error(`No proper config found for ${countryCode}. Verify the config exists`);
    }

    return config;
  }

  private resolveEntireCountryConfig(countryCode: string) {
    const config = _safe(() => _config[countryCode]);
    if (!config) {
      throw new Error(`No proper config found for ${countryCode}. Verify the config exists`);
    }

    return config;
  }
}
